import React from "react";
import { DispatchProp, useDispatch } from "react-redux";
import { uniq, filter, isEmpty } from "../../utils/lodash";
import { ControlProps } from "./BaseControl";
import { ExecutableHtml } from "../shared/ExecutableHtml";
import useTranslatable from "../../hooks/useTranslatable";
import { usePickMentions } from "../../hooks/useMentions";
import { useValidation, isAnswerRequired } from "../../hooks/useValidation";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { isNullOrEmpty } from "../../utils";
import { MentionBuilderDelegate } from "../../models/builders/MentionBuilder";
import { setMultipleVariablesMentions } from "../../redux/actions";

interface Props extends ControlProps, DispatchProp {}

const supportedSingleValueInputTypes = ["text", "hidden", "date", "number", "password"];

function findHtmlVariables(html: string) {
    const regex = /name\s*=\s*(?:["']([^"':]*?:[^"']*?)["']|["']([^"':]*?)["']|(\S+))/gi;
    const variables = [];
    let matches;
    // eslint-disable-next-line no-cond-assign
    while ((matches = regex.exec(html))) {
        variables.push(matches[1] || matches[2] || matches[3]);
    }
    return uniq(variables);
}

function documentFindValue(name: string) {
    const elements = document.getElementsByName(name) as any;
    if (!elements || !elements[0]) {
        return undefined;
    }
    if (["TEXTAREA", "SELECT"].includes(elements[0].tagName)) {
        return elements[0].value;
    }
    if (
        elements[0].tagName === "INPUT" &&
        supportedSingleValueInputTypes.includes(elements[0].type)
    ) {
        return elements[0].value;
    }
    if (["checkbox", "radio"].includes(elements[0].type)) {
        return filter(elements as HTMLInputElement[], (el) => el.checked).map((el) => el.value);
    }
    return undefined;
}

function validateAnswersInHtml(html: string) {
    const variables = findHtmlVariables(html);
    return variables.every((name) => {
        const [variableName, choiceValue, openEnd] = name.split(":");
        // Open-end answers are not required if the open-end choice is not selected.
        if (openEnd && !documentFindValue(variableName)?.includes(choiceValue)) {
            return true;
        }
        return !isEmpty(documentFindValue(name));
    });
}

export function CustomHtmlControl(props: Props) {
    const { question, questionnaire, validateSignal } = props;

    const variableMentions = usePickMentions(question.variableNames);
    const contentTemplate = useTranslatable(question, "content") || "";
    const dispatch = useDispatch();
    const previousValue = React.useRef<string>();

    const answerForMatch = React.useCallback(
        (_value: string, variable: string, choice: string, openEnd: string) => {
            let mentions = variableMentions[variable];
            if (!mentions || !mentions[0]) {
                return "";
            }
            if (choice) {
                if (openEnd) {
                    mentions = mentions.filter((mention) => mention.customChoiceIndex === choice);
                    if (!mentions || !mentions[0] || !mentions[0].openEnd) {
                        return "";
                    }
                    return mentions[0].openEnd;
                }
                return mentions.some((mention) => mention.customChoiceIndex === choice)
                    ? "checked selected"
                    : "";
            }
            return mentions[0].value || "";
        },
        [variableMentions],
    );

    const content = React.useMemo(
        () => contentTemplate.replace(/%(\w+):?(\w*):?(\w*)%/gi, answerForMatch),
        [answerForMatch, contentTemplate],
    );

    const captureAnswers = React.useCallback(
        (html: string) => {
            // Find all mentions in html.
            const variables = findHtmlVariables(html);
            let mentionsByVariable: { [variableName: string]: MentionBuilderDelegate } = {};
            const addMention = (variableName: string, builder: MentionBuilderDelegate) => {
                mentionsByVariable = {
                    ...mentionsByVariable,
                    [variableName]: builder,
                };
            };
            variables.forEach((name) => {
                const elements = document.getElementsByName(name) as any;
                const variable = name.split(":")[0];
                const openEnd = name.indexOf(":O");
                if (!elements || !elements[0]) {
                    return;
                }
                const elValue = elements[0].value;
                if (isNullOrEmpty(elValue) && openEnd == -1) {
                    // element is not openend
                    addMention(variable, (m) => m.clear());
                } else if (elements[0].tagName === "TEXTAREA") {
                    const choice = name.split(":")[1];
                    if (choice && !isNullOrEmpty(elValue)) {
                        addMention(name, (m) =>
                            m.addOpenEndValueByCustomChoiceIndex(choice, elValue),
                        );
                    } else if (!isNullOrEmpty(elValue)) {
                        addMention(variable, (m) => m.set(elValue));
                    }
                } else if (elements[0].tagName === "SELECT") {
                    addMention(variable, (m) => m.set(elValue));
                } else if (supportedSingleValueInputTypes.includes(elements[0].type)) {
                    if (!isNullOrEmpty(elValue)) {
                        addMention(variable, (m) => m.set(elValue));
                    }
                } else if (["checkbox", "radio"].includes(elements[0].type)) {
                    const checked = filter(elements as HTMLInputElement[], (el) => el.checked);
                    addMention(variable, (m) => {
                        m.clear();
                        checked.forEach((c) =>
                            m.add(c.value, null, [...elements].indexOf(c) + 1 + ""),
                        );
                        return m;
                    });
                }
            });
            if (!isEmpty(mentionsByVariable)) {
                dispatch(setMultipleVariablesMentions(mentionsByVariable));
            }
        },
        [dispatch],
    );

    useValidation(validateSignal, () => {
        const answerRequired = isAnswerRequired(question, questionnaire);
        captureAnswers(content);

        if (validateAnswersInHtml(content) || !answerRequired) {
            return new ValidationBuilder().build();
        }

        // Question is not answered, so we fail validation for 1st empty variable
        let firstEmptyVariableName = "";
        for (let i = 0; i < question.variableNames.length; i++) {
            const variableName = question.variableNames[i];
            if (isEmpty(documentFindValue(variableName))) {
                firstEmptyVariableName = variableName;
                break;
            }
        }
        return new ValidationBuilder().fail(firstEmptyVariableName, "IncompleteAnswer").build();
    });

    // Capture the answers before we render new html.
    if (previousValue.current && previousValue.current !== content) {
        captureAnswers(previousValue.current);
    }
    previousValue.current = content;

    return <ExecutableHtml html={content} scriptKey={`${question.name}_content`} />;
}
